import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { Navbar } from './components/common/Navbar';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { BrandDashboard } from './pages/brand/BrandDashboard';
import { SearchSpaces } from './pages/brand/SearchSpaces';
import { SpaceDetail, CartPage, OrdersList, OrderDetail, CampaignsPage, InvoicesPage } from './pages/brand/BrandPages';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminListings, AdminBookings, AdminProviders } from './pages/admin/AdminPages';
import { AdminReports, AdminSettings } from './pages/admin/AdminReportsSettings';
import LandingPage from './pages/LandingPage';

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#e8621a', dark: '#c2490e', light: '#fff3ec' },
    background: { default: '#f8f6f1', paper: '#ffffff' },
    text: { primary: '#1a1410', secondary: '#6b5e4e' },
  },
  typography: {
    fontFamily: 'DM Sans, sans-serif',
    h1: { fontFamily: 'DM Sans, sans-serif' },
    h2: { fontFamily: 'DM Sans, sans-serif' },
    h3: { fontFamily: 'DM Sans, sans-serif' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiMenu: { styleOverrides: { paper: { boxShadow: '0 8px 32px rgba(100,70,30,0.12)', border: '1px solid #e8e2d9' } } },
    MuiMenuItem: { styleOverrides: { root: { fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: '#1a1410', '&:hover': { background: '#f8f6f1' } } } },
    MuiSelect: { styleOverrides: { icon: { color: '#9e8e7e' } } },
    MuiChip: { styleOverrides: { root: { fontFamily: 'DM Sans, sans-serif' } } },
    MuiCheckbox: { styleOverrides: { root: { color: '#e0d8cc' } } },
    MuiSwitch: { defaultProps: { size: 'small' } },
    MuiTableCell: { styleOverrides: { root: { fontFamily: 'DM Sans, sans-serif', borderColor: '#f0ece5' } } },
    MuiAlert: { styleOverrides: { root: { fontFamily: 'DM Sans, sans-serif', borderRadius: 12 } } },
    MuiSlider: { styleOverrides: { root: { color: '#e8621a' } } },
    MuiDivider: { styleOverrides: { root: { borderColor: '#e8e2d9' } } },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode; role?: 'brand' | 'admin' }> = ({ children, role }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) return (
    <div style={{ minHeight: '100vh', background: '#f8f6f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #fde8d8', borderTopColor: '#e8621a', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }}/>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <p style={{ color: '#9e8e7e', fontFamily: 'DM Sans', fontSize: 14, margin: 0 }}>Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === 'admin' ? '/admin/dashboard' : '/brand/dashboard'} replace />;
  return <>{children}</>;
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <><Navbar />{children}</>
);

function App() {
  return (
    <ThemeProvider theme={lightTheme}>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />

              {/* Brand Routes */}
              <Route path="/brand/dashboard" element={<ProtectedRoute role="brand"><Layout><BrandDashboard /></Layout></ProtectedRoute>} />
              <Route path="/brand/search" element={<ProtectedRoute role="brand"><Layout><SearchSpaces /></Layout></ProtectedRoute>} />
              <Route path="/brand/spaces/:id" element={<ProtectedRoute role="brand"><Layout><SpaceDetail /></Layout></ProtectedRoute>} />
              <Route path="/brand/cart" element={<ProtectedRoute role="brand"><Layout><CartPage /></Layout></ProtectedRoute>} />
              <Route path="/brand/orders" element={<ProtectedRoute role="brand"><Layout><OrdersList /></Layout></ProtectedRoute>} />
              <Route path="/brand/orders/:id" element={<ProtectedRoute role="brand"><Layout><OrderDetail /></Layout></ProtectedRoute>} />
              <Route path="/brand/campaigns" element={<ProtectedRoute role="brand"><Layout><CampaignsPage /></Layout></ProtectedRoute>} />
              <Route path="/brand/invoices" element={<ProtectedRoute role="brand"><Layout><InvoicesPage /></Layout></ProtectedRoute>} />

              {/* Admin Routes */}
              <Route path="/admin/dashboard" element={<ProtectedRoute role="admin"><Layout><AdminDashboard /></Layout></ProtectedRoute>} />
              <Route path="/admin/listings" element={<ProtectedRoute role="admin"><Layout><AdminListings /></Layout></ProtectedRoute>} />
              <Route path="/admin/bookings" element={<ProtectedRoute role="admin"><Layout><AdminBookings /></Layout></ProtectedRoute>} />
              <Route path="/admin/providers" element={<ProtectedRoute role="admin"><Layout><AdminProviders /></Layout></ProtectedRoute>} />
              <Route path="/admin/reports" element={<ProtectedRoute role="admin"><Layout><AdminReports /></Layout></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute role="admin"><Layout><AdminSettings /></Layout></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
